import { useEffect, useState, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import Header from './components/Header';
import HyattCollectionRibbon from './components/HyattCollectionRibbon';
import HeroSection from './components/HeroSection';
import InteractiveMap from './components/InteractiveMap';
import StatsSection from './components/StatsSection';
import Footer from './components/Footer';
import TilePanelDialog from './components/TilePanelDialog';
import SOPDialog from './components/SOPDialog';
import VirtualTour from './components/VirtualTour';
// iter-111 — Code-split the two heaviest routes (AdminPage ~3 MB,
// PMPage ~1.5 MB). They are gated behind authenticated routes the
// public landing page never visits, so deferring their bundle gives
// the first-paint route a meaningfully smaller download.
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PMPage = lazy(() => import('./pages/PMPage'));
import { Navigate } from 'react-router-dom';

// iter-109 — Legacy /tasks redirects to the Trello-pure /pm experience.
function TasksRedirect() { return <Navigate to="/pm" replace />; }

// iter-111 — Suspense fallback shown briefly while the lazy-loaded
// AdminPage / PMPage chunk downloads. Minimal markup keeps the
// flash-of-loading-state visually quiet.
function LazyRouteFallback({ label = 'Loading' }) {
  return (
    <div
      data-testid="lazy-route-fallback"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A1A2F',
        color: '#A3D4D4',
        fontFamily: 'Manrope, sans-serif',
        fontSize: '13px',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
      }}
    >
      <span style={{ opacity: 0.85 }}>{label} · Loading…</span>
    </div>
  );
}
import { FALLBACK_AREAS } from './data/resortAreas';
import { LightboxProvider, useLightbox } from './components/LightboxProvider';
import { InstallBanner } from './components/InstallAppButton';
import RoadmapSection from './components/RoadmapSection';
import ReconstructionRoadmap from './components/ReconstructionRoadmap';
import PacerRoadmap from './components/PacerRoadmap';
import PublicCalendarSection from './components/PublicCalendarSection';
import TechnicalPlansDialog from './components/TechnicalPlansDialog';
import PropertyBudgetsDialog from './components/PropertyBudgetsDialog';
import WorkspaceDialog from './components/WorkspaceDialog';
import InsurerAssetsDialog from './components/InsurerAssetsDialog';
import PropertyPermitsDialog from './components/PropertyPermitsDialog';
import RenderCenterDialog from './components/RenderCenterDialog';
import ProposedEnhancementsBanner from './components/ProposedEnhancementsBanner';
import ProposedEnhancementsDialog from './components/ProposedEnhancementsDialog';
import SubcontractorsDialog from './components/SubcontractorsDialog';
import TLDIDialog from './components/TLDIDialog';
import SnaggingListsDialog from './components/SnaggingListsDialog';
import AccessRequestDialog from './components/AccessRequestDialog';
import usePermissions from './lib/usePermissions';
import { accessHintForButton, DASHBOARD_BUTTONS } from './lib/permissions';
import AICredentialsDialog from './components/AICredentialsDialog';
import ProjectSearchDialog from './components/ProjectSearchDialog';
import GuidedTourSession from './components/GuidedTourSession';
import TutorialOverlay from './components/TutorialOverlay';
import QuickUploadButton from './components/QuickUploadButton';
import UpdateAvailableToast from './components/UpdateAvailableToast';
import VersionMismatchBanner from './components/VersionMismatchBanner';
import FreshnessPill from './components/FreshnessPill';
import IdleLogoutManager from './components/IdleLogoutManager';
import AutoRefreshManager from './components/AutoRefreshManager';
import HeavenlyAssistant from './components/HeavenlyAssistant';
import { ChecklistCreatorProvider } from './contexts/ChecklistContext';
import { Toaster } from './components/ui/sonner';
import PropertyDetailsSection from './components/PropertyDetailsSection';
import AtriumPromoSection from './components/AtriumPromoSection';
import AtriumLandingPage from './pages/AtriumLandingPage';
// Side-effect import: installs the global axios response interceptor
// that converts every 403 from a write endpoint into a friendly
// "Viewer mode only" sonner toast, so viewer-role users never crash
// the page by trying to edit something.
import './lib/axiosInterceptors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ContentBlock({ block }) {
  const { open } = useLightbox();
  if (block.type === 'image') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-4" style={block.styles || {}}>
        <img
          src={block.content}
          alt=""
          className="w-full rounded-xl shadow-lg cursor-zoom-in transition-transform hover:scale-[1.01]"
          onClick={() => open(block.content)}
        />
      </div>
    );
  }
  if (block.type === 'heading') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-3" style={block.styles || {}}>
        <h2 className="text-2xl md:text-3xl font-medium" style={{ fontFamily: "'Cormorant Garamond', serif", color: block.styles?.color || '#1A2E2E', textAlign: block.styles?.textAlign || 'center' }}>
          {block.content}
        </h2>
      </div>
    );
  }
  return (
    <div className="max-w-4xl mx-auto px-4 py-2" style={block.styles || {}}>
      <p className="text-base leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif', color: block.styles?.color || '#4A5D5D', textAlign: block.styles?.textAlign || 'center', fontSize: block.styles?.fontSize || '16px' }}>
        {block.content}
      </p>
    </div>
  );
}

function LandingPage() {
  const [areas, setAreas] = useState([]);
  // Controls the "Proposed Crucial Enhancements" fullscreen ownership
  // deck that opens from the wide banner above the InteractiveMap.
  const [enhancementsOpen, setEnhancementsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [slides, setSlides] = useState([]);
  const [contentBlocks, setContentBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStartAreaId, setTourStartAreaId] = useState(null);
  const [plansOpen, setPlansOpen] = useState(false);
  const [budgetsOpen, setBudgetsOpen] = useState(false);
  const [insurerOpen, setInsurerOpen] = useState(false);
  const [permitsOpen, setPermitsOpen] = useState(false);
  const [renderCenterOpen, setRenderCenterOpen] = useState(false);
  const [subcontractorsOpen, setSubcontractorsOpen] = useState(false);
  const [tldiOpen, setTldiOpen] = useState(false);
  const [snaggingOpen, setSnaggingOpen] = useState(false);
  const [projectSearchOpen, setProjectSearchOpen] = useState(false);
  const [aiCredentialsOpen, setAiCredentialsOpen] = useState(false);
  const [searchedAreaId, setSearchedAreaId] = useState(null);
  // Tutorial — auto-launches on first signed-in visit. We poll /auth/me
  // once on mount; if `tutorial_completed` is False the spotlight tour
  // pops up. Backend persists completion so it doesn't replay.
  const [tutorialOpen, setTutorialOpen] = useState(false);
  // iter-111H — Full /auth/me payload (role + departments) for any
  // dialog that needs to check Tier-B permissions client-side
  // (e.g. TLDIDialog read/write gating).
  const [currentUser, setCurrentUser] = useState(null);
  const [workspaceRoot, setWorkspaceRoot] = useState(null);

  // iter-79: auth-state HAS to be reactive. Previously these were
  // captured as plain `const`s from localStorage on mount — so if the
  // user got bounced by the idle-logout watchdog or a token expiry
  // while React stayed mounted, the role state stuck at its stale
  // mount-time value. Symptom users reported: "logout button vanishes,
  // and every padlock locks even though I'm admin" — that was the
  // role going empty without React noticing. We now re-read on the
  // standard `storage` event (cross-tab) AND on our custom
  // `atrium:auth-changed` event (same-tab, fired by AuthGatePage on
  // login).
  const readAdminToken = () => (typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null);
  const readUserRole = () => (typeof window !== 'undefined' ? (localStorage.getItem('user_role') || '') : '');
  const [adminToken, setAdminToken] = useState(readAdminToken);
  const [userRole, setUserRole] = useState(readUserRole);

  useEffect(() => {
    const sync = () => {
      setAdminToken(readAdminToken());
      setUserRole(readUserRole());
    };
    window.addEventListener('storage', sync);
    window.addEventListener('atrium:auth-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('atrium:auth-changed', sync);
    };
  }, []);

  // iter-80 — Dashboard button overrides from the admin CMS. Loaded
  // once on mount; the {key: {label, size, visible, color, font_size_px}}
  // shape is merged with the hard-coded defaults inside `dashBtn()`.
  // Anonymous viewers still get the right buttons because the GET is
  // public.
  const [dashboardButtons, setDashboardButtons] = useState({});
  const [customDashboardButtons, setCustomDashboardButtons] = useState([]);
  // iter-87 — Departmental tile panel (files + links).
  const [tilePanel, setTilePanel] = useState(null);
  // iter-114 — SOPs module dialog.
  const [sopOpen, setSopOpen] = useState(false);
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('sop')) setSopOpen(true);
    } catch { /* swallow */ }
  }, []);
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard-buttons`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setDashboardButtons(d.buttons || {}); })
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard-buttons-custom`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setCustomDashboardButtons(d.custom || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const dashBtn = (key, defaults = {}) => {
    const cfg = dashboardButtons[key] || {};
    return {
      label: cfg.label ?? defaults.label,
      visible: cfg.visible !== false,
      size: cfg.size ?? defaults.size ?? 'md',
      color: cfg.color ?? defaults.color,
      fontSizePx: cfg.font_size_px ?? defaults.font_size_px ?? 14,
    };
  };

  // Padlock visibility is driven by the user's role, not just whether
  // they have a token. Viewers and editors see the same padlocks that
  // guests do on confidential branches (HR, Finance, Budgets, IT, Brand
  // Standards) — full access is reserved for admin and engineer.
  const hasFullAccess = ['admin', 'engineer'].includes(userRole);

  // Tier-B departmental permissions. `canAccess(button)` decides which
  // buttons are unlocked for THIS user (admins always pass). Locked
  // buttons show a clickable padlock that opens the access-request dialog.
  const { canAccess, isDisabledByAdmin, refresh: refreshPerms } = usePermissions();
  const [accessReq, setAccessReq] = useState(null); // {button, label} | null

  // First-login tutorial bootstrap. Runs once when an authenticated user
  // hits the public landing — we ask /auth/me whether the tutorial flag is
  // still False, and if so pop the overlay. Skipped for guests.
  useEffect(() => {
    if (!adminToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${adminToken}` } });
        if (cancelled) return;
        if (data) setCurrentUser(data);
        if (data && data.tutorial_completed === false) {
          // Wait a moment for the page to render so the spotlight has
          // measurable elements to anchor to.
          setTimeout(() => setTutorialOpen(true), 1200);
        }
      } catch (_) { /* noop — guests / expired tokens stay quiet */ }
    })();
    return () => { cancelled = true; };
  }, [adminToken]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [areasRes, settingsRes, slidesRes, blocksRes] = await Promise.all([
          axios.get(`${API}/areas`),
          axios.get(`${API}/settings`),
          axios.get(`${API}/slides`),
          axios.get(`${API}/content-blocks`),
        ]);
        setAreas(areasRes.data);
        setSettings(settingsRes.data);
        setSlides(slidesRes.data);
        setContentBlocks(blocksRes.data);
      } catch (e) {
        console.error('Failed to fetch data:', e);
        setAreas(FALLBACK_AREAS);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // iter-119 — Global per-button kill-switch effect. Injects a tiny
  // <style> tag that hides disabled buttons for non-admin-likes, and
  // greys them out (with a "DISABLED" badge) for admins/engineers so
  // they can spot+re-enable from the admin panel in one glance. Targets
  // each button by its data-testid value to avoid touching every JSX
  // block individually.
  useEffect(() => {
    const adminLike = ['admin', 'engineer', 'ownership', 'top_leader'].includes(userRole);
    const idToTestid = Object.fromEntries(DASHBOARD_BUTTONS.map((b) => [b.id, b.testid]));
    const disabled = (DASHBOARD_BUTTONS || [])
      .filter((b) => isDisabledByAdmin(b.id))
      .map((b) => idToTestid[b.id])
      .filter(Boolean);
    let style = document.getElementById('atrium-button-toggles-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'atrium-button-toggles-style';
      document.head.appendChild(style);
    }
    if (disabled.length === 0) {
      style.textContent = '';
      return;
    }
    const selectors = disabled.map((tid) => `[data-testid="${tid}"]`).join(',\n');
    if (adminLike) {
      style.textContent = `
${selectors} {
  opacity: 0.45 !important;
  filter: grayscale(70%);
  position: relative;
}
${disabled.map((tid) => `[data-testid="${tid}"]::after`).join(',\n')} {
  content: 'DISABLED';
  position: absolute;
  top: -8px;
  right: -8px;
  background: #DC2626;
  color: white;
  font-size: 9px;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 9999px;
  letter-spacing: 0.06em;
  font-family: Manrope, sans-serif;
  z-index: 5;
  pointer-events: none;
  box-shadow: 0 2px 6px rgba(220,38,38,0.4);
}`;
    } else {
      style.textContent = `${selectors} { display: none !important; }`;
    }
  }, [isDisabledByAdmin, userRole]);

  // Both "Explore" pills (header + hero) dispatch this event so visitors
  // can jump to any widget via the existing Project Search dialog. Wired
  // here at the top level so it survives across all hash routes.
  useEffect(() => {
    const handler = () => setProjectSearchOpen(true);
    window.addEventListener('atrium:open-project-search', handler);
    return () => window.removeEventListener('atrium:open-project-search', handler);
  }, []);

  // My To-Do dialog deep-links snag items here so the user lands directly
  // in the Snagging Lists dialog. Wired at top level to survive routes.
  useEffect(() => {
    const handler = () => setSnaggingOpen(true);
    window.addEventListener('atrium:open-snagging', handler);
    return () => window.removeEventListener('atrium:open-snagging', handler);
  }, []);


  // Departmental padlocks dispatch `atrium:request-access` so any nested
  // helper (WorkspaceTile, button wrapper) can open the request dialog
  // without prop-drilling the setter through. Cleaned up on unmount.
  useEffect(() => {
    const handler = (e) => {
      const { button, label } = e.detail || {};
      if (button) setAccessReq({ button, label });
    };
    window.addEventListener('atrium:request-access', handler);
    return () => window.removeEventListener('atrium:request-access', handler);
  }, []);

  const bgColor = settings?.background_color || '#F4F1EB';
  const heroBlocks = contentBlocks.filter((b) => b.position === 'hero_below');
  const aboveMapBlocks = contentBlocks.filter((b) => b.position === 'above_maps');
  const belowMapBlocks = contentBlocks.filter((b) => b.position === 'below_maps');
  const footerBlocks = contentBlocks.filter((b) => b.position === 'footer_above');

  return (
    <div data-testid="app-root" style={{ backgroundColor: bgColor }}>
      <Header settings={settings} />
      <main className="pt-16">
        <HyattCollectionRibbon />
        <HeroSection settings={settings} slides={slides} />
        {heroBlocks.map((b) => <ContentBlock key={b.id} block={b} />)}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: settings?.primary_color || '#1F4FBE', borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <>
            {/* Tutorial top-middle button — always visible (signed-in or
                guest), prominently positioned so first-time visitors can
                trigger the guided tour without hunting for it. */}
            <div className="flex justify-center pt-3">
              <button
                data-testid="tutorial-top-btn"
                onClick={() => setTutorialOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold text-white transition-all hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  fontFamily: 'Manrope',
                  boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                }}
                title="Take the guided tour with the Heavenly Assistant"
              >
                <span style={{ fontSize: '14px' }}>✨</span> Take the Tutorial
              </button>
            </div>

            {/* Main CTAs (big pills) */}
            <div className="flex flex-wrap justify-center items-center gap-3 pt-4 pb-2">
              <button
                data-testid="virtual-tour-btn"
                onClick={() => setTourOpen(true)}
                className="flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl animate-pulse"
                style={{ backgroundColor: '#8B5CF6', fontFamily: 'Manrope', boxShadow: '0 4px 25px rgba(139,92,246,0.4)' }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                Start Virtual Tour
              </button>
              <button
                data-testid="technical-plans-btn"
                onClick={() => {
                  if (!adminToken) {
                    if (window.confirm('Property Technical Plans require you to be signed in. Sign in to continue?')) window.location.href = '/admin';
                    return;
                  }
                  if (!canAccess('property-technical-plans')) {
                    openAccessRequest('property-technical-plans', 'Property Technical Plans');
                    return;
                  }
                  setPlansOpen(true);
                }}
                className="relative flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl"
                style={{ backgroundColor: '#1F4FBE', fontFamily: 'Manrope', boxShadow: '0 4px 20px rgba(0,61,165,0.35)' }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                  <line x1="9" y1="18" x2="15" y2="18" />
                  <line x1="9" y1="12" x2="11" y2="12" />
                </svg>
                Property Technical Plans
                {!canAccess('property-technical-plans') && <LockIcon button="property-technical-plans" label="Property Technical Plans" />}
              </button>
              <button
                data-testid="property-budgets-btn"
                onClick={() => {
                  if (!adminToken) {
                    if (window.confirm('Property Budgets requires you to be signed in. Sign in to continue?')) window.location.href = '/admin';
                    return;
                  }
                  if (!canAccess('property-budgets')) {
                    openAccessRequest('property-budgets', 'Property Budgets');
                    return;
                  }
                  setBudgetsOpen(true);
                }}
                className="relative flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl"
                style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope', boxShadow: '0 4px 20px rgba(0,61,165,0.4)' }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
                Property Budgets
                {!canAccess('property-budgets') && <LockIcon button="property-budgets" label="Property Budgets" />}
              </button>
              <WorkspaceTile testid="ws-designs" label="Designs & Visualisations by Areas" rootId="designs" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} />
              <IfAIEnabled>
                <button
                  data-testid="render-center-btn"
                  onClick={() => {
                    if (!adminToken) {
                      if (window.confirm('The Design & Render Center requires you to be signed in. Sign in to continue?')) window.location.href = '/admin';
                      return;
                    }
                    setRenderCenterOpen(true);
                  }}
                  className="relative flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl"
                  style={{
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    fontFamily: 'Manrope',
                    boxShadow: '0 4px 16px rgba(99,102,241,0.35)',
                  }}
                  title="Generate concept renders and walkthrough videos with AI"
                >
                  <span style={{ fontSize: '16px' }}>✨</span>
                  Design &amp; Render Center
                  {!adminToken && <CornerPadlock />}
                </button>
              </IfAIEnabled>
              <button
                data-testid="insurer-assets-btn"
                onClick={() => {
                  if (!adminToken) {
                    if (window.confirm('Insurer Assets · Disposal requires you to be signed in. Continue?')) window.location.href = '/admin';
                    return;
                  }
                  if (!canAccess('insurer-assets')) {
                    openAccessRequest('insurer-assets', 'Insurer Assets · Disposal');
                    return;
                  }
                  setInsurerOpen(true);
                }}
                className="relative flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl"
                style={{ backgroundColor: '#B45309', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(180,83,9,0.3)' }}
                title="Assets approved by insurer for disposal"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
                Insurer Assets · Disposal
                {!canAccess('insurer-assets') && <CornerPadlock button="insurer-assets" label="Insurer Assets · Disposal" />}
              </button>

              {/* iter-79: Emergency Response — sits next to Insurer Assets
                  on the main row so the GM can reach it in 1 click. Also
                  mirrored as a sub-node inside the Security workspace. */}
              {(() => {
                const cfg = dashBtn('emergency-response', { label: 'Emergency Response', color: '#7F1D1D', font_size_px: 14 });
                if (!cfg.visible && userRole !== 'admin' && userRole !== 'engineer') return null;
                const sizeClass = cfg.size === 'lg' ? 'px-8 py-4' : cfg.size === 'sm' ? 'px-4 py-2' : 'px-6 py-3';
                return (
                  <button
                    data-testid="emergency-response-btn"
                    onClick={() => {
                      if (!adminToken) {
                        if (window.confirm('Emergency Response requires you to be signed in. Continue?')) window.location.href = '/admin';
                        return;
                      }
                      setWorkspaceRoot('emergency-response');
                    }}
                    className={`relative flex items-center gap-2.5 ${sizeClass} rounded-full font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl`}
                    style={{ background: `linear-gradient(135deg, #DC2626 0%, ${cfg.color} 100%)`, fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(220,38,38,0.4)', fontSize: `${cfg.fontSizePx}px`, opacity: cfg.visible ? 1 : 0.4 }}
                    title="Hurricane / fire / medical / security response playbooks and live incident log"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    {cfg.label}
                    {!adminToken && <CornerPadlock />}
                  </button>
                );
              })()}

              {/* iter-79: Post Hurricane Damage — quick-access hub for
                  damage inventories, before/after photos, restoration
                  progress, and insurance evidence. */}
              {(() => {
                const cfg = dashBtn('post-hurricane-damage', { label: 'Post Hurricane Damage', color: '#0EA5E9', font_size_px: 14 });
                if (!cfg.visible && userRole !== 'admin' && userRole !== 'engineer') return null;
                const sizeClass = cfg.size === 'lg' ? 'px-8 py-4' : cfg.size === 'sm' ? 'px-4 py-2' : 'px-6 py-3';
                return (
                  <button
                    data-testid="post-hurricane-damage-btn"
                    onClick={() => {
                      if (!adminToken) {
                        if (window.confirm('Post Hurricane Damage requires you to be signed in. Continue?')) window.location.href = '/admin';
                        return;
                      }
                      setWorkspaceRoot('post-hurricane-damage');
                    }}
                    className={`relative flex items-center gap-2.5 ${sizeClass} rounded-full font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl`}
                    style={{ background: `linear-gradient(135deg, #1F4FBE 0%, ${cfg.color} 100%)`, fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(31,79,190,0.35)', fontSize: `${cfg.fontSizePx}px`, opacity: cfg.visible ? 1 : 0.4 }}
                    title="Hurricane damage inventory · before/after photos · restoration progress · insurance evidence"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2c2.5 0 4.8.9 6.6 2.4" />
                      <path d="M22 2L12 12" />
                      <polyline points="22 8 22 2 16 2" />
                    </svg>
                    {cfg.label}
                    {!adminToken && <CornerPadlock />}
                  </button>
                );
              })()}

              {/* iter-80: NPR Reports / Initiatives — hub for newsworthy
                  property reports, strategic initiatives, ownership
                  communications, and supporting documents. */}
              {(() => {
                const cfg = dashBtn('npr-reports', { label: 'NPR Reports / Initiatives', color: '#4338CA', font_size_px: 14 });
                if (!cfg.visible && userRole !== 'admin' && userRole !== 'engineer') return null;
                const sizeClass = cfg.size === 'lg' ? 'px-8 py-4' : cfg.size === 'sm' ? 'px-4 py-2' : 'px-6 py-3';
                return (
                  <button
                    data-testid="npr-reports-btn"
                    onClick={() => {
                      if (!adminToken) {
                        if (window.confirm('NPR Reports / Initiatives requires you to be signed in. Continue?')) window.location.href = '/admin';
                        return;
                      }
                      setWorkspaceRoot('npr-reports');
                    }}
                    className={`relative flex items-center gap-2.5 ${sizeClass} rounded-full font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl`}
                    style={{ background: `linear-gradient(135deg, #7C3AED 0%, ${cfg.color} 100%)`, fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(124,58,237,0.35)', fontSize: `${cfg.fontSizePx}px`, opacity: cfg.visible ? 1 : 0.4 }}
                    title="Non-Package Revenue — all hotel revenue outside the all-inclusive package (spa, premium bars, excursions, retail, paid upgrades). Reports, outlet performance, targets, and initiatives."
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="9" y1="13" x2="15" y2="13" />
                      <line x1="9" y1="17" x2="13" y2="17" />
                    </svg>
                    {cfg.label}
                    {!adminToken && <CornerPadlock />}
                  </button>
                );
              })()}

              {/* iter-82: Reports / Articles — published reports, property
                  newsletters, ownership comms, press releases, articles. */}
              {(() => {
                const cfg = dashBtn('reports-articles', { label: 'Reports / Articles', color: '#0891B2', font_size_px: 14 });
                if (!cfg.visible && userRole !== 'admin' && userRole !== 'engineer') return null;
                const sizeClass = cfg.size === 'lg' ? 'px-8 py-4' : cfg.size === 'sm' ? 'px-4 py-2' : 'px-6 py-3';
                return (
                  <button
                    data-testid="reports-articles-btn"
                    onClick={() => {
                      if (!adminToken) {
                        if (window.confirm('Reports / Articles requires you to be signed in. Continue?')) window.location.href = '/admin';
                        return;
                      }
                      setWorkspaceRoot('reports-articles');
                    }}
                    className={`relative flex items-center gap-2.5 ${sizeClass} rounded-full font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl`}
                    style={{ background: `linear-gradient(135deg, #06B6D4 0%, ${cfg.color} 100%)`, fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(6,182,212,0.35)', fontSize: `${cfg.fontSizePx}px`, opacity: cfg.visible ? 1 : 0.4 }}
                    title="Published reports, articles, newsletters, press releases, ownership communications"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
                      <path d="M18 14h-8M15 18h-5M10 6h8v4h-8z" />
                    </svg>
                    {cfg.label}
                    {!adminToken && <CornerPadlock />}
                  </button>
                );
              })()}

              {/* iter-82: Audits — External (Mystery Shopper, Condition
                  Audit) + Internal (Operations, Financial, Brand
                  Standards). Sub-hubs render automatically from the
                  workspace tree. */}
              {(() => {
                const cfg = dashBtn('audits', { label: 'Audits', color: '#7C2D12', font_size_px: 14 });
                if (!cfg.visible && userRole !== 'admin' && userRole !== 'engineer') return null;
                const sizeClass = cfg.size === 'lg' ? 'px-8 py-4' : cfg.size === 'sm' ? 'px-4 py-2' : 'px-6 py-3';
                return (
                  <button
                    data-testid="audits-btn"
                    onClick={() => {
                      if (!adminToken) {
                        if (window.confirm('Audits requires you to be signed in. Continue?')) window.location.href = '/admin';
                        return;
                      }
                      setWorkspaceRoot('audits');
                    }}
                    className={`relative flex items-center gap-2.5 ${sizeClass} rounded-full font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl`}
                    style={{ background: `linear-gradient(135deg, #C2410C 0%, ${cfg.color} 100%)`, fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(194,65,12,0.35)', fontSize: `${cfg.fontSizePx}px`, opacity: cfg.visible ? 1 : 0.4 }}
                    title="External (Mystery Shopper · Condition Audit) and Internal (Operations · Financial · Brand Standards) audits"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 11l3 3L22 4" />
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                    </svg>
                    {cfg.label}
                    {!adminToken && <CornerPadlock />}
                  </button>
                );
              })()}

              {/* iter-82: Safety — workplace safety meetings, incident
                  reports, hazard log, PPE inventory, training records,
                  compliance. Distinct from Security (asset protection)
                  and Emergency Response (forward-looking playbooks). */}
              {(() => {
                const cfg = dashBtn('safety', { label: 'Safety', color: '#16A34A', font_size_px: 14 });
                if (!cfg.visible && userRole !== 'admin' && userRole !== 'engineer') return null;
                const sizeClass = cfg.size === 'lg' ? 'px-8 py-4' : cfg.size === 'sm' ? 'px-4 py-2' : 'px-6 py-3';
                return (
                  <button
                    data-testid="safety-btn"
                    onClick={() => {
                      if (!adminToken) {
                        if (window.confirm('Safety requires you to be signed in. Continue?')) window.location.href = '/admin';
                        return;
                      }
                      setWorkspaceRoot('safety');
                    }}
                    className={`relative flex items-center gap-2.5 ${sizeClass} rounded-full font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl`}
                    style={{ background: `linear-gradient(135deg, #22C55E 0%, ${cfg.color} 100%)`, fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(34,197,94,0.35)', fontSize: `${cfg.fontSizePx}px`, opacity: cfg.visible ? 1 : 0.4 }}
                    title="Workplace safety — meetings · incident reports · hazard log · PPE · training records · compliance"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                    {cfg.label}
                    {!adminToken && <CornerPadlock />}
                  </button>
                );
              })()}
              {customDashboardButtons.map((b) => {
                if (!b || !b.id) return null;
                if (!b.visible && userRole !== 'admin' && userRole !== 'engineer') return null;
                const sizeClass = b.size === 'lg' ? 'px-8 py-4' : b.size === 'sm' ? 'px-4 py-2' : 'px-6 py-3';
                const executePrimary = (tile) => {
                  if (tile.action_type === 'workspace') {
                    if (!adminToken) {
                      if (window.confirm(`${tile.label} requires you to be signed in. Continue?`)) window.location.href = '/admin';
                      return;
                    }
                    if (tile.action_target) setWorkspaceRoot(tile.action_target);
                  } else if (tile.action_type === 'url') {
                    if (tile.action_target) window.open(tile.action_target, '_blank', 'noopener,noreferrer');
                  }
                };
                const handler = () => {
                  // iter-87 — When a tile has attached files OR curated
                  // links, the click opens a panel showing both AND a
                  // "open primary" button. When neither is set, the tile
                  // jumps straight to its workspace folder / external URL
                  // exactly like before.
                  const hasFiles = (b.tile_files || []).length > 0;
                  const hasLinks = (b.tile_links || []).length > 0;
                  if (hasFiles || hasLinks) {
                    setTilePanel(b);
                    return;
                  }
                  executePrimary(b);
                };
                return (
                  <button
                    key={b.id}
                    data-testid={`custom-dashboard-${b.id}`}
                    onClick={handler}
                    className={`relative flex items-center gap-2.5 ${sizeClass} rounded-full font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl`}
                    style={{
                      backgroundColor: b.color || '#1F4FBE',
                      fontFamily: 'Manrope',
                      boxShadow: `0 4px 16px ${(b.color || '#1F4FBE')}55`,
                      fontSize: `${b.font_size_px || 14}px`,
                      opacity: b.visible ? 1 : 0.4,
                    }}
                    title={b.label}
                  >
                    {b.label}
                  </button>
                );
              })}
              <button
                data-testid="property-permits-btn"
                onClick={() => {
                  if (!adminToken) {
                    if (window.confirm('Licenses & Permits requires you to be signed in. Continue?')) window.location.href = '/admin';
                    return;
                  }
                  if (!canAccess('property-permits')) {
                    openAccessRequest('property-permits', 'Licenses & Permits');
                    return;
                  }
                  setPermitsOpen(true);
                }}
                className="relative flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl"
                style={{ backgroundColor: '#1E40AF', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(30,64,175,0.3)' }}
                title="Property licenses and permits"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <path d="M9 14l2 2 4-4" />
                </svg>
                Licenses &amp; Permits
                {!canAccess('property-permits') && <CornerPadlock button="property-permits" label="Licenses & Permits" />}
              </button>
              <button
                data-testid="project-search-btn"
                onClick={() => setProjectSearchOpen(true)}
                className="relative flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl"
                style={{ backgroundColor: '#0F766E', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(15,118,110,0.3)' }}
                title="Search areas, widgets, and projects"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                Project Search
              </button>
              <button
                data-testid="subcontractors-btn"
                onClick={() => {
                  if (!adminToken) {
                    if (window.confirm('Subcontractors requires you to be signed in. Sign in to continue?')) window.location.href = '/admin';
                    return;
                  }
                  if (!canAccess('subcontractors')) {
                    openAccessRequest('subcontractors', 'Subcontractors');
                    return;
                  }
                  setSubcontractorsOpen(true);
                }}
                className="relative flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl"
                style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(0,61,165,0.35)' }}
                title="GC subcontracted vendors — contracts, invoices, scope of work"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 18a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4v2H2v-2z" />
                  <path d="M12 14V8" />
                  <path d="M8 8h8l-1-5H9l-1 5z" />
                </svg>
                Subcontractors
                {!canAccess('subcontractors') && <CornerPadlock button="subcontractors" label="Subcontractors" />}
              </button>
              <button
                data-testid="tldi-btn"
                onClick={() => {
                  if (!adminToken) {
                    if (window.confirm('TLDI requires you to be signed in. Sign in to continue?')) window.location.href = '/admin';
                    return;
                  }
                  if (!canAccess('tldi')) {
                    openAccessRequest('tldi', 'TLDI');
                    return;
                  }
                  setTldiOpen(true);
                }}
                className="relative flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl"
                style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(0,61,165,0.35)' }}
                title="Tourism and Leisure Development International — general contractor file vault"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {/* Hard-hat silhouette */}
                  <path d="M4 18h16" />
                  <path d="M6 18v-3a6 6 0 0 1 12 0v3" />
                  <path d="M12 9V5" />
                  <path d="M9 5h6" />
                </svg>
                TLDI
                {!canAccess('tldi') && <CornerPadlock button="tldi" label="TLDI" />}
              </button>
              <button
                data-testid="snagging-btn"
                onClick={() => {
                  if (!adminToken) {
                    if (window.confirm('Snagging Lists requires you to be signed in. Sign in to continue?')) window.location.href = '/admin';
                    return;
                  }
                  setSnaggingOpen(true);
                }}
                className="relative flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl"
                style={{ backgroundColor: '#B91C1C', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(185,28,28,0.35)' }}
                title="Site QC defects tracker — log, triage, and resolve snags"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {/* Checklist with a defect mark */}
                  <path d="M9 11l3 3 8-8" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
                Snagging Lists
                {!adminToken && <CornerPadlock />}
              </button>
              <button
                data-testid="ai-credentials-btn"
                onClick={() => {
                  if (!adminToken) {
                    if (window.confirm('AI Credentials requires you to be signed in. Sign in to continue?')) window.location.href = '/admin';
                    return;
                  }
                  setAiCredentialsOpen(true);
                }}
                className="relative flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl ai-creds-glow group"
                style={{ backgroundColor: '#D97706', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(217,119,6,0.35)' }}
                title="Use your own OpenAI / Gemini / Claude keys for higher-tier AI generation"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                </svg>
                AI Credentials
                {!adminToken && <CornerPadlock />}
                {/* iter-79: hover tooltip nudging users to plug in their own keys for best results */}
                <span
                  data-testid="ai-creds-tooltip"
                  className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-1.5 rounded-lg text-[11px] font-bold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: '#0A1A2F', color: 'white', fontFamily: 'Manrope', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}
                >
                  Enter your own AI API / credentials for best results
                  <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0" style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #0A1A2F' }} />
                </span>
              </button>
              {/* iter-114 — SOPs module. Opens a dialog with 13 department
                  buttons and a top-right SOP maker (AI-generated SOPs that
                  save to `sops` collection + mirror a link into the
                  matching dept tile folder). */}
              {(() => {
                const cfg = dashBtn('sops', { label: "SOP's", color: '#0F766E', font_size_px: 14 });
                if (!cfg.visible && userRole !== 'admin' && userRole !== 'engineer') return null;
                const sizeClass = cfg.size === 'lg' ? 'px-8 py-4' : cfg.size === 'sm' ? 'px-4 py-2' : 'px-6 py-3';
                return (
                  <button
                    data-testid="sops-btn"
                    onClick={() => {
                      if (!adminToken) {
                        if (window.confirm("SOP's requires you to be signed in. Continue?")) window.location.href = '/admin';
                        return;
                      }
                      setSopOpen(true);
                    }}
                    className={`relative flex items-center gap-2.5 ${sizeClass} rounded-full font-bold text-white transition-all hover:-translate-y-1 hover:shadow-xl`}
                    style={{ background: `linear-gradient(135deg, #14B8A6 0%, ${cfg.color} 100%)`, fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(15,118,110,0.4)', fontSize: `${cfg.fontSizePx}px`, opacity: cfg.visible ? 1 : 0.4 }}
                    title="Standard Operating Procedures · per-department library with an AI SOP maker"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="8" y1="13" x2="16" y2="13" />
                      <line x1="8" y1="17" x2="13" y2="17" />
                    </svg>
                    {cfg.label}
                    {!adminToken && <CornerPadlock />}
                  </button>
                );
              })()}
            </div>

            {/* Department workspaces (compact tiles). Admin-only branches
                (HR, IT, Finance, Engineering, Brand Standards) carry a
                padlock badge for any role below admin/engineer. */}
            <div className="flex flex-wrap justify-center items-center gap-2 pb-3 max-w-5xl mx-auto">
              <WorkspaceTile testid="ws-it" label="IT/Systems" rootId="it-projects" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-hr" label="HR" rootId="hr-projects" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-engineering" label="Engineering" rootId="engineering" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-finance" label="Finance" rootId="finance" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-brand" label="Brand Standards" rootId="brand-standards" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-grounds" label="Grounds" rootId="grounds" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-procurement" label="Procurement Planning" rootId="procurement-planning" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-fb" label="F&B" rootId="fb-workspace" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-rooms" label="Rooms" rootId="rooms" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-spa" label="Spa" rootId="spa" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-security" label="Security" rootId="security" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-entertainment" label="Entertainment" rootId="entertainment" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-water-sports" label="Water Sports" rootId="water-sports" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-sales" label="Sales" rootId="sales" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
              <WorkspaceTile testid="ws-laundry" label="Laundry" rootId="laundry" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} compact />
            </div>

            {/* OS&E + Storage row — visible to admins AND editors so the
                lists/inventories admins create are reachable for
                editing by anyone with write permission. Viewers see a
                padlock affordance via their existing 2-tier perms when
                they try to mutate; the tiles themselves are listed so
                the directory is discoverable. */}
            {(() => {
              const role = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
              if (!role || role === 'viewer') return null;
              return (
                <div className="flex flex-wrap justify-center items-center gap-3 pb-3">
                  <WorkspaceTile testid="ws-ose" label="OS&E Lists" rootId="ose-lists" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} />
                  <WorkspaceTile testid="ws-storage" label="Storage" rootId="storage" hasFullAccess={hasFullAccess} adminToken={adminToken} setWorkspaceRoot={setWorkspaceRoot} canAccess={canAccess} />
                </div>
              );
            })()}

            {/* Designs visualisations tile also gets the canAccess gating for consistency.
                It's universal in the BUTTON_DEPTS map so it always passes — but wiring
                the prop ensures future re-gating works without another patch. */}

            {/* Admin-only quick upload affordance under the tiles row */}
            <div className="flex justify-center pb-6">
              <QuickUploadButton />
            </div>

            {aboveMapBlocks.map((b) => <ContentBlock key={b.id} block={b} />)}
            <PropertyDetailsSection />

            {/* Big "Proposed Crucial Enhancements" banner above the
                Interactive Map — glows light-blue, opens the fullscreen
                ownership deck on click. Rendered ONLY when at least one
                area carries an enhancement worth surfacing, so the
                banner never lies about being a click-worthy CTA. */}
            {areas?.some((a) => (a.enhancements || []).some((e) => e.kind === 'enhancement')
                              || (a.enhancements_roi || '').trim()
                              || (a.roi_investment || '').trim()) && (
              <ProposedEnhancementsBanner onOpen={() => setEnhancementsOpen(true)} />
            )}

            <InteractiveMap
              areas={areas}
              onAreasUpdate={setAreas}
              settings={settings}
              slides={slides}
              onStartTour={(area) => { setTourStartAreaId(area?.id || null); setTourOpen(true); }}
            />
            {belowMapBlocks.map((b) => <ContentBlock key={b.id} block={b} />)}
          </>
        )}
        <RoadmapSection />
        <PacerRoadmap />
        <ReconstructionRoadmap />
        <PublicCalendarSection />
        <div id="stats-section">
          <StatsSection settings={settings} areas={areas} />
        </div>
        {footerBlocks.map((b) => <ContentBlock key={b.id} block={b} />)}
      </main>
      <ProposedEnhancementsDialog
        open={enhancementsOpen}
        onClose={() => setEnhancementsOpen(false)}
        areas={areas}
      />
      <AtriumPromoSection />
      <Footer settings={settings} />
      <FreshnessPill />
      {tourOpen && <VirtualTour areas={areas} slides={slides} startAreaId={tourStartAreaId} onClose={() => { setTourOpen(false); setTourStartAreaId(null); }} />}
      <TechnicalPlansDialog open={plansOpen} onClose={setPlansOpen} />
      <PropertyBudgetsDialog open={budgetsOpen} onClose={setBudgetsOpen} />
      {workspaceRoot && (
        <WorkspaceDialog
          open={!!workspaceRoot}
          onClose={() => setWorkspaceRoot(null)}
          rootId={workspaceRoot}
        />
      )}
      {/* iter-114 — SOPs module dialog. */}
      <SOPDialog open={sopOpen} onClose={() => setSopOpen(false)} />
      {/* iter-87 — per-tile attachments panel. Opened when a custom
          dashboard button has tile_files or tile_links configured. */}
      <TilePanelDialog
        open={!!tilePanel}
        onClose={() => setTilePanel(null)}
        tile={tilePanel}
        onPrimaryAction={(tile) => {
          setTilePanel(null);
          // Reuse the same execute path as a no-attachment click.
          if (tile.action_type === 'workspace') {
            if (!adminToken) {
              if (window.confirm(`${tile.label} requires you to be signed in. Continue?`)) window.location.href = '/admin';
              return;
            }
            if (tile.action_target) setWorkspaceRoot(tile.action_target);
          } else if (tile.action_type === 'url') {
            if (tile.action_target) window.open(tile.action_target, '_blank', 'noopener,noreferrer');
          }
        }}
      />
      <InsurerAssetsDialog
        open={insurerOpen}
        onClose={() => setInsurerOpen(false)}
        hasFullAccess={hasFullAccess}
        adminToken={adminToken}
      />
      <PropertyPermitsDialog
        open={permitsOpen}
        onClose={() => setPermitsOpen(false)}
        hasFullAccess={hasFullAccess || canAccess('property-permits')}
        adminToken={adminToken}
      />
      <RenderCenterDialog
        open={renderCenterOpen}
        onClose={() => setRenderCenterOpen(false)}
        hasFullAccess={!!adminToken}
        adminToken={adminToken}
        areas={areas}
      />
      <SubcontractorsDialog
        open={subcontractorsOpen}
        onClose={() => setSubcontractorsOpen(false)}
        hasFullAccess={hasFullAccess}
        adminToken={adminToken}
      />
      <TLDIDialog
        open={tldiOpen}
        onClose={() => setTldiOpen(false)}
        adminToken={adminToken}
        userRole={currentUser?.role}
        userDepartments={currentUser?.departments || []}
      />
      <SnaggingListsDialog
        open={snaggingOpen}
        onClose={() => setSnaggingOpen(false)}
        token={adminToken}
        currentRole={currentUser?.role}
        areas={areas}
      />
      <AICredentialsDialog
        open={aiCredentialsOpen}
        onClose={() => setAiCredentialsOpen(false)}
      />

      {/* Guided Tour Auto-Pilot — listens for `atrium:start-guided-tour`
          (fired by the "Explore" header pill) and walks the visitor
          through the top hotspots with Heavenly narrating each stop. */}
      <GuidedTourSession
        areas={areas}
        onAreaOpen={(area) => {
          // Forward to InteractiveMap via the existing open-area event so
          // the AreaDetailDialog pops without duplicating the dialog tree.
          try {
            window.dispatchEvent(new CustomEvent('open-area', { detail: { areaId: area.id } }));
          } catch (e) { /* no-op */ }
        }}
      />
      <ProjectSearchDialog
        open={projectSearchOpen}
        onClose={() => setProjectSearchOpen(false)}
        areas={areas}
        onPickArea={(aid) => { setProjectSearchOpen(false); setSearchedAreaId(aid); }}
      />
      {/* Heavenly Assistant guided tour — auto-pops on first signed-in
          visit (per-user flag) and can be replayed any time via the
          tutorial helper button (lower-right of the page footer). */}
      <TutorialOverlay
        token={adminToken}
        autoOpen={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
      />
      {adminToken && (
        <button
          data-testid="replay-tutorial-btn"
          onClick={() => setTutorialOpen(true)}
          className="fixed bottom-4 right-4 z-[80] px-3 py-2 rounded-full text-[10px] font-bold text-white inline-flex items-center gap-1.5 transition hover:-translate-y-0.5"
          style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', fontFamily: 'Manrope', boxShadow: '0 4px 12px rgba(99,102,241,0.4)' }}
          title="Replay the in-app tutorial"
        >
          ✨ Replay tutorial
        </button>
      )}
      {searchedAreaId && (() => {
        // After Project Search picks an area, scroll the page so the matching
        // hotspot/widget is visible. Click handlers on MapHotspot already
        // open the AreaDetailDialog, so we dispatch a custom event the
        // hotspot listens for to open itself.
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('open-area', { detail: { areaId: searchedAreaId } }));
          setSearchedAreaId(null);
        }, 50);
        return null;
      })()}
      <InstallBanner />

      {/* Locked-button access-request dialog. The padlock on every gated
          button opens this with the right button key, so users can ask
          an admin for 1h / 2h / 1d temporary access. */}
      <AccessRequestDialog
        open={!!accessReq}
        onClose={() => { setAccessReq(null); refreshPerms(); }}
        button={accessReq?.button}
        buttonLabel={accessReq?.label}
      />
    </div>
  );
}

// `setAccessReq` for non-LandingPage components is exposed via a module-scope
// CustomEvent — kept minimal so WorkspaceTile / button wrappers don't need
// to be prop-drilled with the setter.
function openAccessRequest(button, label) {
  try {
    window.dispatchEvent(new CustomEvent('atrium:request-access', { detail: { button, label } }));
  } catch (e) { /* no-op */ }
}

// Set of workspace root IDs that the backend treats as admin/engineer-only
// (see `admin_only=True` in workspace_routes.SEED_TREE). Matching this set
// on the frontend keeps the padlock icon in sync with the API's 403 reads.
const ADMIN_ONLY_WORKSPACE_ROOTS = new Set([
  'it-projects', 'hr-projects', 'engineering', 'finance', 'brand-standards',
]);

// Compact workspace tile used on the landing page for the department/workspace shortcuts.
// Padlock visibility is now driven by Tier-B departmental permissions via
// `canAccess(testid)`. Admins always pass. Locked tiles show a clickable
// padlock that opens the access-request dialog (1h/2h/1d). Guests see a
// sign-in prompt instead.
function WorkspaceTile({ testid, label, rootId, hasFullAccess, adminToken, setWorkspaceRoot, canAccess, compact = false }) {
  const allowed = canAccess ? canAccess(testid) : true;
  const isAdminOnly = ADMIN_ONLY_WORKSPACE_ROOTS.has(rootId);
  // Show padlock when the user can't see this tile by department.
  // Guests (no token) still get a sign-in confirm rather than the request dialog.
  const locked = !allowed;
  const onClick = () => {
    if (!adminToken) {
      if (window.confirm('This workspace requires you to be signed in. Continue to sign in?')) {
        window.location.href = '/admin';
      }
      return;
    }
    if (!allowed) {
      openAccessRequest(testid, label);
      return;
    }
    openWorkspace({ setWorkspaceRoot, rootId, hasFullAccess, adminToken, isAdminOnly });
  };
  const base = 'relative flex items-center gap-2 rounded-full font-bold transition-all hover:-translate-y-0.5';
  const sizing = compact ? 'px-4 py-2 text-xs' : 'px-6 py-3 text-sm';
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`${base} ${sizing}`}
      style={{
        backgroundColor: compact ? 'white' : '#003DA5',
        color: compact ? '#003DA5' : 'white',
        border: compact ? '1px solid #A3C4EE' : 'none',
        fontFamily: 'Manrope',
        boxShadow: compact ? '0 1px 4px rgba(0,61,165,0.08)' : '0 4px 20px rgba(0,61,165,0.3)',
      }}
    >
      {label}
      {locked && <CornerPadlock button={testid} label={label} />}
    </button>
  );
}

// Small padlock badge anchored to the top-right corner of a tile/button.
function CornerPadlock({ button, label }) {
  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (button) openAccessRequest(button, label);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (button) openAccessRequest(button, label);
    }
  };
  return (
    // Rendered as a span (NOT a <button>) because the padlock lives
    // INSIDE another <button> (the workspace tile / pill). Nesting two
    // <button>s breaks HTML hydration and the outer button captures the
    // click before the inner one can fire. The role + tabIndex + onKeyDown
    // keep keyboard accessibility intact.
    // iter-96 — tooltip now spells out exactly which roles + departments
    // unlock the tile so engineers / admins / dept members understand
    // why they're seeing (or not seeing) the padlock.
    <span
      role="button"
      tabIndex={0}
      aria-label={button ? `Request access to ${label || button}` : 'Locked'}
      title={
        button
          ? `${accessHintForButton(button)}\n\nClick to request temporary access.`
          : 'Admin access required'
      }
      data-testid={button ? `padlock-${button}` : 'corner-padlock'}
      onClick={onClick}
      onKeyDown={onKeyDown}
      style={{
        position: 'absolute',
        top: -6,
        right: -6,
        width: 18,
        height: 18,
        borderRadius: '50%',
        backgroundColor: '#1A2E2E',
        color: '#FDE68A',
        border: '2px solid white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        zIndex: 2,
        cursor: button ? 'pointer' : 'default',
        padding: 0,
      }}
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 8h-1V6a5 5 0 0 0-10 0v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zM9 6a3 3 0 0 1 6 0v2H9V6z" />
      </svg>
    </span>
  );
}

// Inline lock icon used inside big pill buttons (after the label text).
function LockIcon({ button, label }) {
  return <CornerPadlock button={button} label={label} />;
}

// Guard a workspace root open. Confidential roots (HR/Finance/IT/Engineering/
// Brand Standards) require admin or engineer. Open roots require any
// signed-in user. The bouncer routes guests + restricted roles to the
// auth gate with a friendly, role-aware confirm dialog.
function openWorkspace({ setWorkspaceRoot, rootId, hasFullAccess, adminToken, isAdminOnly }) {
  if (isAdminOnly && !hasFullAccess) {
    if (window.confirm('This workspace is reserved for admin and engineer roles. Sign in with the right credentials to continue?')) {
      window.location.href = '/admin';
    }
    return;
  }
  if (!adminToken) {
    if (window.confirm('This workspace requires you to be signed in. Continue to sign in?')) {
      window.location.href = '/admin';
    }
    return;
  }
  setWorkspaceRoot(rootId);
}

import AuthGatePage from './pages/AuthGatePage';
import ErrorBoundary from './components/ErrorBoundary';
import { AIVisibilityProvider, IfAIEnabled } from './contexts/AIVisibilityContext';

// Global axios defaults — guard against runaway requests that would
// otherwise spin forever on flaky networks. 45s is plenty for media
// uploads (chunked uploads override per-call) but cuts dead connections.
axios.defaults.timeout = 45000;

/* Token-watcher wrapper that mounts HeavenlyAssistant once globally.
   Reads `admin_token` from localStorage on each render and listens for
   storage events so the assistant reactively updates after a sign-in or
   sign-out without forcing a full page reload.
   Hidden on `/` (Atrium marketing landing) — that route is for prospective
   customers and shouldn't expose the in-app assistant or bug-report UI. */
function HeavenlyAssistantGlobal() {
  const [token, setToken] = useState(() =>
    typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null
  );
  const [pathname, setPathname] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  useEffect(() => {
    const sync = () => {
      setToken(localStorage.getItem('admin_token'));
      setPathname(window.location.pathname);
    };
    window.addEventListener('storage', sync);
    // Same-tab login doesn't fire `storage`; poll once a second so login
    // in the same tab also picks up. Cheap (no network), sub-millisecond.
    const t = setInterval(sync, 1000);
    return () => {
      window.removeEventListener('storage', sync);
      clearInterval(t);
    };
  }, []);
  if (pathname === '/') return null;
  return (
    <IfAIEnabled>
      <HeavenlyAssistant token={token} />
    </IfAIEnabled>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AIVisibilityProvider>
          <LightboxProvider>
            <ChecklistCreatorProvider>
              <Routes>
                {/* Atrium marketing landing — public root entry point.
                    Marketing site for the Atrium platform itself. The "Login to
                    Atrium" button routes to /login (the AuthGate). */}
                <Route path="/" element={<AtriumLandingPage />} />
                <Route path="/login" element={<AuthGatePage />} />
                <Route path="/admin" element={<AuthGatePage />} />
                <Route path="/app" element={<LandingPage />} />
                <Route path="/admin/dashboard" element={
                  <Suspense fallback={<LazyRouteFallback label="Admin" />}>
                    <AdminPage />
                  </Suspense>
                } />
                <Route path="/tasks" element={<TasksRedirect />} />
                <Route path="/pm" element={
                  <Suspense fallback={<LazyRouteFallback label="Projects" />}>
                    <PMPage />
                  </Suspense>
                } />
              </Routes>
              <UpdateAvailableToast />
              <VersionMismatchBanner />
              {/* iter-110 — IdleLogoutManager disabled per user request:
                  no screen should time out. Keeping AutoRefreshManager
                  for service-worker hot-reload of static assets. */}
              <AutoRefreshManager />
              {/* Heavenly Assistant — mounted ONCE at app level so the floating
                  avatar + bug button + chat widget travel with the user across
                  every route (auth gate, public landing, admin dashboard).
                  Reads admin_token from localStorage internally and re-renders
                  when storage events fire so a sign-in/out picks up cleanly. */}
              <HeavenlyAssistantGlobal />
              <Toaster richColors position="top-right" />
            </ChecklistCreatorProvider>
          </LightboxProvider>
        </AIVisibilityProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
